/**
 * A character's page.
 *
 * To a reader it is where the character can be supported: a name, and an address to
 * send to. Every character has one from the moment the book knows they exist, so a
 * reader is never told to come back later.
 *
 * To the person holding the character — and, for now, to the author — it also shows
 * the twelve words behind that address. That is the wallet itself: whoever has them
 * can move the money, which is why they sit behind a deliberate press rather than on
 * the page, and why the warning beside them says plainly what they are.
 *
 * Nothing here reveals who the holder is. That is the whole arrangement: readers can
 * pay a character without anyone — including the author — learning who stands behind it.
 *
 * It is a page of the book, not a profile screen, so it wears the reader's paper and
 * ink. Second-person copy stays out of gendered verb forms: this is addressed to a
 * real person whose gender the book does not know.
 */

import { ArrowBack, ContentCopy } from '@mui/icons-material'
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useParams } from 'react-router-dom'

import { useReaderLightPalette } from '../hooks/useReaderLightPalette'
import { CharacterDetail, apiService } from '../services/api'
import { READER_PAPER_BACKGROUND_SX } from '../styles/paperBackground'
import { logger } from '../utils/logger'

const BOOK_FONT = "'SBL Hebrew', 'Times New Roman', serif"

/** The ornament the reader already knows, from the chapter dividers. */
const Ornament: React.FC = () => (
  <svg width="120" height="16" viewBox="0 0 120 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M60 2 L64 8 L60 14 L56 8 Z" fill="var(--ornament-fill)" opacity="0.5" />
    <path d="M52 8 Q40 3 8 8" stroke="var(--ornament-stroke)" strokeWidth="1" fill="none" strokeLinecap="round" />
    <path d="M68 8 Q80 3 112 8" stroke="var(--ornament-stroke)" strokeWidth="1" fill="none" strokeLinecap="round" />
  </svg>
)

/**
 * Hebrew makes the verb agree with the number, so this is a sentence rather than a
 * template with a figure dropped into it.
 *
 * The empty case names who has not sent: printed directly under a button that says
 * "send", a subjectless "nothing has been sent yet" reads as an accusation aimed at the
 * person looking at it. Nobody should be told off for having just arrived at a page.
 */
function describeReceived(amount: number): string {
  if (amount === 0) {
    return 'אף אחד עוד לא שלח לכאן כלום'
  }
  if (amount === 1) {
    return 'עד עכשיו הגיע לכאן דולר אחד'
  }
  return `עד עכשיו הגיעו לכאן ${amount.toLocaleString('he-IL', { maximumFractionDigits: 2 })} דולר`
}

const quietButtonSx = {
  color: 'var(--reader-text-primary)',
  fontFamily: BOOK_FONT,
  textTransform: 'none' as const,
  fontSize: '0.95rem',
  px: 2.5,
  border: '1px solid var(--reader-sidebar-divider)',
  borderRadius: '6px',
  '&:hover': { backgroundColor: 'var(--reader-hover)', borderColor: 'var(--ornament-stroke)' },
}

const copyIconSx = {
  color: 'var(--reader-text-submuted)',
  '&:hover': { color: 'var(--reader-text-muted)', backgroundColor: 'var(--reader-hover)' },
}

const CharacterPage: React.FC = () => {
  const { bookId, tagId } = useParams<{ bookId: string; tagId: string }>()
  const [character, setCharacter] = useState<CharacterDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [phraseShown, setPhraseShown] = useState(false)

  useReaderLightPalette()

  useEffect(() => {
    if (!bookId || !tagId) {
      return
    }
    apiService
      .getCharacter(bookId, tagId)
      .then(setCharacter)
      .catch((err) => logger.error('Failed to load character', err))
      .finally(() => setLoading(false))
  }, [bookId, tagId])

  const copy = (value: string, said: string) => {
    navigator.clipboard.writeText(value)
    toast.success(said)
  }

  const page = (children: React.ReactNode) => (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        ...READER_PAPER_BACKGROUND_SX,
        display: 'flex',
        justifyContent: 'center',
        px: 3,
        py: { xs: 6, md: 10 },
      }}
    >
      {/* The way out, in the corner where a reader looks for it — no label, since an
          arrow in that position says the whole thing on its own. */}
      {bookId && (
        <Tooltip title="אל הספר">
          <IconButton
            component={Link}
            to={`/read/${bookId}`}
            aria-label="אל הספר"
            sx={{
              position: 'fixed',
              top: { xs: 12, md: 20 },
              left: { xs: 12, md: 20 },
              color: 'var(--reader-text-secondary)',
              border: '1px solid var(--reader-sidebar-divider)',
              backgroundColor: 'var(--reader-page-bg)',
              '&:hover': { backgroundColor: 'var(--reader-hover)', borderColor: 'var(--ornament-stroke)' },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ width: '100%', maxWidth: 520 }}>{children}</Box>
    </Box>
  )

  if (loading) {
    return page(
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={28} sx={{ color: 'var(--reader-spinner)' }} />
      </Box>,
    )
  }

  if (!character) {
    return page(
      <Typography sx={{ fontFamily: BOOK_FONT, color: 'var(--reader-text-muted)', textAlign: 'center' }}>
        הדמות הזאת לא נמצאה.
      </Typography>,
    )
  }

  return page(
    <Stack spacing={4} alignItems="center">
      <Stack spacing={1} alignItems="center">
        <Typography
          sx={{
            fontFamily: BOOK_FONT,
            fontSize: { xs: '1.9rem', md: '2.3rem' },
            color: 'var(--reader-text-primary)',
            letterSpacing: '0.03em',
            lineHeight: 1.3,
          }}
        >
          {character.name}
        </Typography>
        <Ornament />
      </Stack>

      {character.authorNote && (
        <Typography
          sx={{
            fontFamily: BOOK_FONT,
            fontSize: '1.05rem',
            lineHeight: 1.9,
            color: 'var(--reader-text-primary)',
            whiteSpace: 'pre-wrap',
            textAlign: 'center',
          }}
        >
          {character.authorNote}
        </Typography>
      )}

      {character.walletAddress && (
        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <Typography sx={{ fontFamily: BOOK_FONT, fontSize: '0.95rem', color: 'var(--reader-text-muted)' }}>
            הארנק של {character.name}
          </Typography>
          {/* Paper-colored QR: a white tile would be a hole punched in the page. */}
          <Box
            sx={{
              p: 2,
              borderRadius: '8px',
              border: '1px solid var(--reader-sidebar-divider)',
              backgroundColor: 'var(--reader-page-bg)',
              lineHeight: 0,
            }}
          >
            {/* Literal ink, not a token: the QR is drawn into an SVG fill attribute,
                and a scanner needs the contrast to be certain rather than inherited. */}
            <QRCodeSVG value={character.walletAddress} size={168} bgColor="transparent" fgColor="#3a2e22" />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: '100%' }}>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                color: 'var(--reader-text-secondary)',
                wordBreak: 'break-all',
              }}
            >
              {character.walletAddress}
            </Typography>
            <Tooltip title="העתקת הכתובת">
              <IconButton size="small" onClick={() => copy(character.walletAddress!, 'הכתובת הועתקה')} sx={copyIconSx}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {/* What was sent, not what is left: the balance drops when the character
              withdraws, and a page reporting it would tell the next reader that nobody
              had given anything. Zero is worth saying out loud — it is a fact about the
              wallet. `null` is not: it means the indexer was unreachable, and a page
              that does not know must not claim nothing came. */}
          {character.receivedUsdc != null && (
            <Typography sx={{ fontFamily: BOOK_FONT, fontSize: '0.95rem', color: 'var(--reader-text-secondary)' }}>
              {describeReceived(character.receivedUsdc)}
            </Typography>
          )}
          <Typography sx={{ fontFamily: BOOK_FONT, fontSize: '0.8rem', color: 'var(--reader-text-submuted)' }}>
            USDC ברשת Base
          </Typography>
        </Stack>
      )}

      {/* The wallet itself. Behind a press, because showing it is showing the money. */}
      {character.mnemonic && (
        <Stack
          spacing={1.5}
          alignItems="center"
          sx={{
            width: '100%',
            pt: 3,
            borderTop: '1px solid var(--reader-sidebar-divider)',
          }}
        >
          {phraseShown ? (
            <>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.95rem',
                  lineHeight: 2,
                  color: 'var(--reader-text-primary)',
                  textAlign: 'center',
                  direction: 'ltr',
                  px: 2,
                }}
              >
                {character.mnemonic}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="העתקת המילים">
                  <IconButton size="small" onClick={() => copy(character.mnemonic!, 'המילים הועתקו')} sx={copyIconSx}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  onClick={() => setPhraseShown(false)}
                  size="small"
                  sx={{
                    color: 'var(--reader-text-muted)',
                    fontFamily: BOOK_FONT,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    '&:hover': { backgroundColor: 'var(--reader-hover)' },
                  }}
                >
                  הסתר
                </Button>
              </Stack>
              <Typography
                sx={{
                  fontFamily: BOOK_FONT,
                  fontSize: '0.8rem',
                  lineHeight: 1.9,
                  color: 'var(--reader-text-submuted)',
                  textAlign: 'center',
                  maxWidth: 400,
                }}
              >
                שתים־עשרה המילים האלה <b>הן</b> הארנק. מי שמחזיק בהן יכול להוציא ממנו את הכסף. כדאי להעתיק אותן למקום
                בטוח ולא לשלוח אותן לאיש.
              </Typography>
            </>
          ) : (
            <Button onClick={() => setPhraseShown(true)} disableElevation sx={quietButtonSx}>
              הצגת המילים של הארנק
            </Button>
          )}
        </Stack>
      )}
    </Stack>,
  )
}

export default CharacterPage
